import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Typography,
    Box,
} from '@mui/material';
import { useSnackbar } from 'src/components/snackbar';
import { useServerRequest } from 'src/hooks/useServerRequest';
import Iconify from 'src/components/iconify';
import { useDispatch } from 'react-redux';
import { addFilesToCaution } from 'src/redux/slices/cautionSlice';
import { useLocales } from 'src/locales';

interface FileUploadDialogProps {
    open: boolean;
    cautionId: number;
    onClose: () => void;
}

const MAX_FILES = 10;
const MAX_TOTAL_SIZE = 20 * 1024 * 1024; // 20 MB in bytes

const AddFiles: React.FC<FileUploadDialogProps> = ({ open, cautionId, onClose }) => {
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const { enqueueSnackbar } = useSnackbar();
    const { makeRequest } = useServerRequest();
    const dispatch = useDispatch();
    const { t } = useLocales();

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { files } = event.target;

        if (!files) return;

        const newFiles = Array.from(files);

        const totalSize = newFiles.reduce((acc, file) => acc + file.size, 0) + selectedFiles.reduce((acc, file) => acc + file.size, 0);

        if (selectedFiles.length + newFiles.length > MAX_FILES) {
            enqueueSnackbar(t('cautions.files.file_upload.max_files_error', { maxFiles: MAX_FILES }), { variant: 'error' });
            return;
        }

        if (totalSize > MAX_TOTAL_SIZE) {
            enqueueSnackbar(t('cautions.files.file_upload.max_size_error'), { variant: 'error' });
            return;
        }

        setSelectedFiles((prevFiles) => [...prevFiles, ...newFiles]);
    };

    const handleFileRemove = (index: number) => {
        setSelectedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
    };

    const handleUpload = async () => {
        if (selectedFiles.length === 0) {
            enqueueSnackbar(t('cautions.files.file_upload.no_files_error'), { variant: 'error' });
            return;
        }

        try {
            const formData = new FormData();
            formData.append('caution_id', cautionId.toString());
            selectedFiles.forEach((file) => {
                formData.append('files[]', file);
            });

            const response = await makeRequest('/files/upload', {
                method: 'POST',
                data: formData,
            });

            if (response.success && response.data) {
                setSelectedFiles([]);
                dispatch(addFilesToCaution({
                    cautionId,
                    files: response.data.files,
                }));
                onClose();
            }
        } catch (error) {
            enqueueSnackbar(t('cautions.files.file_upload.upload_failed'), { variant: 'error' });
        }
    };

    if (!cautionId) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{t('cautions.files.file_upload.title')}</DialogTitle>
            <DialogContent>
                <Typography variant="subtitle1">{t('cautions.files.file_upload.subtitle')}</Typography>
                <Box my={2}>
                    <Button variant="contained" component="label">
                        {t('cautions.files.file_upload.select_files')}
                        <input
                            type="file"
                            multiple
                            hidden
                            onChange={handleFileChange}
                        />
                    </Button>
                </Box>
                <List>
                    {selectedFiles.map((file, index) => (
                        <ListItem key={index} secondaryAction={
                            <IconButton edge="end" onClick={() => handleFileRemove(index)}>
                                <Iconify icon="solar:trash-bin-trash-bold" />
                            </IconButton>
                        }>
                            <ListItemText primary={file.name} secondary={`${(file.size / 1024 / 1024).toFixed(2)} MB`} />
                        </ListItem>
                    ))}
                </List>
                {selectedFiles.length === 0 && (
                    <Typography variant="body2" color="textSecondary">
                        {t('cautions.files.file_upload.no_files_selected')}
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="outlined" color="inherit">
                    {t('common.button.action.cancel')}
                </Button>
                <Button onClick={handleUpload} variant="contained" disabled={selectedFiles.length === 0}>
                    {t('common.button.action.upload')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddFiles;